import { metricsQueryPolicy } from './metricsQueryPolicy';
import { useQueries } from '@tanstack/react-query';
import { metricsApi } from '../api/metrics';
import { qk } from '../../lib/queryKeys';
import type { MetricsStatsParams } from '../api/types';
import type { ComparisonGroup } from './useComparisonGroups';

/**
 * Fetches headline stats for each comparison group in parallel (one request per
 * group), using the existing stats endpoint. Used to assemble the per-group
 * metric snapshot the AI summary narrates. Reuses the same query keys as
 * useMetricsStats so requests dedupe/cache with the rest of the app.
 */
export const useComparisonStats = (groups: ComparisonGroup[], metrics: string[]) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: MetricsStatsParams = { ...group.filterParams, metrics };
      return {
        queryKey: qk.metrics.stats(params),
        queryFn: ({ signal }) => metricsApi.getStats(params, signal),
        ...metricsQueryPolicy,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return groups.map((group, index) => ({
    group,
    data: results[index]?.data,
    isLoading: results[index]?.isLoading ?? false,
    isError: results[index]?.isError ?? false,
  }));
};
